import {
  SavedSkeleton,
  useSlidesLibraryStore,
} from "@modules/slides-library/shared";
import { cn } from "@lib/cn";
import {
  IconChevronRight,
  IconUser,
  IconUsersGroup,
} from "@tabler/icons-react";
import { useGetFolders } from "../hooks";
import { SortableChildren } from "./recursive-sidebar-folder";

const SECTION_META = {
  "user-slides": { label: "My slides", icon: IconUser },
  "team-slides": { label: "Team slides", icon: IconUsersGroup },
} as const;

export const SidebarSavedTab = ({
  type,
  showHeader = true,
}: {
  type: "user-slides" | "team-slides";
  showHeader?: boolean;
}) => {
  const { folders, isLoading } = useGetFolders();
  const folderId = useSlidesLibraryStore((s) => s.folderId);
  const setFolderId = useSlidesLibraryStore((s) => s.setFolderId);
  const isOpen = useSlidesLibraryStore(
    (s) => !!s.expandedSavedTabs[type],
  );
  const toggleSavedTab = useSlidesLibraryStore((s) => s.toggleSavedTab);
  const { label, icon: SectionIcon } = SECTION_META[type];
  const isRootActive = folderId === type;

  if (isLoading) return <SavedSkeleton.Sidebar />;

  const items =
    type === "user-slides" ? folders.userFolders : folders.teamFolders;
  const hasChildren = !!items?.length;

  return (
    <div className="tw-flex tw-flex-col tw-gap-0.5 tw-py-2">
      {showHeader && (
        <div
          onClick={() => setFolderId(type)}
          className={cn(
            "tw-flex tw-h-8 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-md tw-bg-transparent tw-px-6 tw-pr-1 tw-font-sans tw-text-[14px] tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-100",
            isRootActive && "tw-bg-blue-100",
          )}
        >
          <SectionIcon
            className="tw-size-4 tw-shrink-0 tw-text-blue"
            stroke={1}
          />
          <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-left">
            {label}
          </span>
          {hasChildren && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleSavedTab(type);
              }}
              className="tw-flex tw-size-6 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-transparent tw-p-0 tw-outline-none"
            >
              <IconChevronRight
                className={cn(
                  "tw-size-3.5 tw-text-blue tw-opacity-50 tw-transition-transform",
                  isOpen && "tw-rotate-90",
                )}
                stroke={1.5}
              />
            </button>
          )}
        </div>
      )}
      {hasChildren && (!showHeader || isOpen) && (
        <SortableChildren folders={items!} level={0} parentId={type} />
      )}
    </div>
  );
};
